import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import HowToPlayModal from "./HowToPlayModal";
import StatsModal from "./StatsModal";

function Header() {
  const [showStats, setShowStats] = useState(false);
  const [showHowToPlay, setShowHowToPlay] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
    }

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  useEffect(() => {
    if (loading) return;
    if (user) return;
    if (!localStorage.getItem("hasVisitedGoalGuesser")) {
      setShowHowToPlay(true);
      localStorage.setItem("hasVisitedGoalGuesser", "true");
    }
  }, [loading, user]);

  const handleProfileClick = () => {
    if (loading) return;
    if (user) {
      setShowUserDropdown((open) => !open);
      return;
    }
    navigate("/login");
  };

  const handleLogout = () => {
    logout();
    setShowUserDropdown(false);
    navigate("/");
  };

  return (
    <>
      <header className="top-header">
        <div className="top-header-content">
          <div className="top-header-left-icons">
            <button
              type="button"
              className="top-header-icon-button top-header-howtoplay-btn"
              onClick={() => setShowHowToPlay(true)}
              aria-label="How to play"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </button>
            <a
              href="https://twitter.com/goalguesser"
              target="_blank"
              rel="noopener noreferrer"
              className="top-header-icon-button"
              aria-label="Goal Guesser on X"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 1200 1227"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M714.163 519.284 1160.89 0h-105.86L667.137 450.887 357.328 0H0l468.492 681.821L0 1226.37h105.866l409.649-476.152 327.157 476.152H1200L714.137 519.284h.026ZM569.23 687.828l-47.46-67.894L144.011 79.694h162.604l304.797 436.14 47.46 67.894 396.2 566.721H892.468L569.23 687.854v-.026Z" />
              </svg>
            </a>
          </div>

          <div className="top-header-brand">
            <Link to="/" className="top-header-title-link" aria-label="Go to game">
              <div className="top-header-title">GOAL GUESSER</div>
            </Link>
            <div className="top-header-subtitle">The Daily Soccer Goal Guessing Game.</div>
          </div>

          <div className="top-header-user-wrapper" ref={dropdownRef}>
            <button
              type="button"
              className="top-header-icon-button"
              onClick={handleProfileClick}
              aria-label={user ? "Open account menu" : "Go to login page"}
              aria-haspopup={user ? "menu" : undefined}
              aria-expanded={user ? showUserDropdown : undefined}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.9"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M20 21a8 8 0 0 0-16 0" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            </button>
            {!loading && user && (
              <div className="top-header-username-label" title={user.username}>
                {user.username}
              </div>
            )}
            {!loading && user && showUserDropdown && (
              <div className="top-header-dropdown" role="menu" aria-label="Account menu">
                <button
                  type="button"
                  className="top-header-dropdown-item"
                  onClick={() => {
                    setShowStats(true);
                    setShowUserDropdown(false);
                  }}
                >
                  Stats
                </button>
                <button
                  type="button"
                  className="top-header-dropdown-item"
                  onClick={handleLogout}
                >
                  Log Out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>
      <HowToPlayModal isOpen={showHowToPlay} onClose={() => setShowHowToPlay(false)} />
      <StatsModal isOpen={showStats} onClose={() => setShowStats(false)} />
    </>
  );
}

// Legacy header is intentionally kept for future relocation/replacement.
export function LegacyHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showHowToPlay, setShowHowToPlay] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const headerRef = useRef(null);
  const dropdownRef = useRef(null);
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    function handleClickOutside(e) {
      if (headerRef.current && !headerRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowUserDropdown(false);
      }
    }
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const closeMenu = () => setMenuOpen(false);

  const handleLogout = () => {
    logout();
    closeMenu();
    setShowUserDropdown(false);
    navigate("/");
  };

  return (
    <>
      <header className="header" ref={headerRef}>
        <div className="header-content">
          <Link to="/">
            <img
              src="/images/goalGuesserLogo.png"
              alt="Goal Guesser"
              className="header-logo"
            />
          </Link>
          <div
            className="hamburger"
            id="hamburger"
            onClick={() => setMenuOpen((o) => !o)}
            aria-label="Menu"
          >
            <div className="hamburger-line" />
            <div className="hamburger-line" />
            <div className="hamburger-line" />
          </div>
          <nav className={`header-nav ${menuOpen ? "active" : ""}`} id="headerNav">
            <div
              className="header-item"
              onClick={() => {
                setShowHowToPlay(true);
                closeMenu();
              }}
            >
              How To Play
            </div>
            <a
              href="https://twitter.com/goalguesser"
              target="_blank"
              rel="noopener noreferrer"
              className="header-item header-x-link"
              onClick={closeMenu}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                <path d="M12.6.75h2.454l-5.36 6.142L16 15.25h-4.937l-3.867-5.07-4.425 5.07H.316l5.733-6.57L0 .75h5.063l3.495 4.633L12.601.75Zm-.86 13.028h1.36L4.323 2.145H2.865z"/>
              </svg>
            </a>
            {!loading && user && (
              <div
                className="header-item"
                onClick={() => {
                  setShowStats(true);
                  closeMenu();
                }}
              >
                Stats
              </div>
            )}
            {!loading && (
              user ? (
                <div className="header-user-wrapper" ref={dropdownRef}>
                  <div
                    className="header-item header-username"
                    onClick={() => setShowUserDropdown((o) => !o)}
                  >
                    {user.username}
                    <svg
                      className={`header-arrow ${showUserDropdown ? "open" : ""}`}
                      xmlns="http://www.w3.org/2000/svg"
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </div>
                  {showUserDropdown && (
                    <div className="header-dropdown">
                      <div className="header-dropdown-item" onClick={handleLogout}>
                        Log Out
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <Link to="/login" className="header-item header-login-link" onClick={closeMenu}>
                  Log In
                </Link>
              )
            )}
          </nav>
        </div>
      </header>
      <HowToPlayModal
        isOpen={showHowToPlay}
        onClose={() => setShowHowToPlay(false)}
      />
      <StatsModal isOpen={showStats} onClose={() => setShowStats(false)} />
    </>
  );
}

export default Header;
